import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { simulationsAPI } from '../lib/api';
import { toast } from 'sonner';
import { ClipboardCheck, Shield, Heart, Building, Laptop, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';

const AssurancePage = () => {
  const [checking, setChecking] = useState(false);
  const [formData, setFormData] = useState({
    a_rc_pro: false,
    a_mutuelle: false,
    a_prevoyance: false,
    a_multirisque: false,
    a_cyber: false
  });
  const [result, setResult] = useState(null);

  const handleCheck = async () => {
    setChecking(true);
    try {
      const response = await simulationsAPI.checklistAssurance(formData);
      setResult(response.data);
    } catch (error) {
      toast.error('Erreur lors de l\'analyse');
    } finally {
      setChecking(false);
    }
  };

  const assurances = [
    { key: 'a_rc_pro', label: 'Responsabilité Civile Professionnelle', icon: Shield, description: 'Couvre les dommages causés à des tiers dans le cadre de votre activité' },
    { key: 'a_mutuelle', label: 'Complémentaire Santé (Mutuelle)', icon: Heart, description: 'Complète les remboursements de la Sécurité sociale' },
    { key: 'a_prevoyance', label: 'Prévoyance (IJ, Invalidité, Décès)', icon: Shield, description: 'Protège vos revenus en cas d\'arrêt de travail ou d\'invalidité' },
    { key: 'a_multirisque', label: 'Assurance Multirisque Professionnelle', icon: Building, description: 'Couvre vos locaux, matériel et stocks professionnels' },
    { key: 'a_cyber', label: 'Assurance Cyber-risques', icon: Laptop, description: 'Protection contre les cyberattaques et pertes de données' }
  ];

  const getScoreColor = (score, total) => {
    const percent = (score / total) * 100;
    if (percent < 40) return 'text-red-600';
    if (percent < 60) return 'text-amber-600';
    if (percent < 80) return 'text-blue-600';
    return 'text-emerald-600';
  };

  const getNiveauColor = (niveau) => {
    const colors = {
      'Critique': 'bg-red-100 text-red-800 border-red-200',
      'Insuffisant': 'bg-amber-100 text-amber-800 border-amber-200',
      'Correct': 'bg-blue-100 text-blue-800 border-blue-200',
      'Bon': 'bg-emerald-100 text-emerald-800 border-emerald-200'
    };
    return colors[niveau] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6" data-testid="assurance-page">
      <div>
        <h1 className="text-3xl font-bold font-['Manrope']">Checklist Assurance</h1>
        <p className="text-muted-foreground">Vérifiez si votre couverture est suffisante</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Checklist */}
        <Card>
          <CardHeader>
            <CardTitle className="font-['Manrope'] flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5" />
              Vos assurances actuelles
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {assurances.map((assurance) => (
              <div key={assurance.key} className="flex items-start justify-between gap-4 p-4 border rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                    <assurance.icon className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div>
                    <Label htmlFor={assurance.key} className="font-medium cursor-pointer">
                      {assurance.label}
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">{assurance.description}</p>
                  </div>
                </div>
                <Switch
                  id={assurance.key}
                  checked={formData[assurance.key]}
                  onCheckedChange={(checked) => setFormData({ ...formData, [assurance.key]: checked })}
                  data-testid={`switch-${assurance.key}`}
                />
              </div>
            ))}

            <Button 
              onClick={handleCheck} 
              className="w-full" 
              disabled={checking}
              data-testid="check-assurance-btn"
            >
              {checking ? 'Analyse...' : 'Analyser ma couverture'}
            </Button>
          </CardContent>
        </Card>

        {/* Résultat */}
        <Card>
          <CardHeader>
            <CardTitle className="font-['Manrope']">Analyse de votre couverture</CardTitle>
          </CardHeader>
          <CardContent>
            {result ? (
              <div className="space-y-6">
                {/* Score */}
                <div className="text-center p-6 bg-muted/30 rounded-xl">
                  <div className={`text-5xl font-bold font-['Manrope'] mb-2 ${getScoreColor(result.score, result.total)}`}>
                    {result.score}/{result.total}
                  </div>
                  <div className={`inline-block px-4 py-1 rounded-full border ${getNiveauColor(result.niveau_protection)}`}>
                    {result.niveau_protection}
                  </div>
                  <Progress 
                    value={result.pourcentage} 
                    className="h-3 mt-4"
                  />
                </div>

                {/* Détails */}
                <div className="space-y-3">
                  {Object.entries(result.details).map(([key, detail]) => (
                    <div 
                      key={key} 
                      className={`p-4 rounded-lg border ${detail.status ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {detail.status ? (
                            <CheckCircle className="h-5 w-5 text-emerald-600" />
                          ) : (
                            <XCircle className="h-5 w-5 text-red-600" />
                          )}
                          <span className="font-medium">{detail.label}</span>
                        </div>
                        <span className="text-sm text-muted-foreground">{detail.cout_moyen}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">{detail.importance}</p>
                    </div>
                  ))}
                </div>

                {/* Priorités */}
                {result.priorites.length > 0 && (
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="h-5 w-5 text-amber-600" />
                      <span className="font-medium text-amber-900">Priorités à souscrire</span>
                    </div>
                    <ul className="space-y-1">
                      {result.priorites.map((p) => (
                        <li key={p} className="text-sm text-amber-800 flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                          {result.details[p]?.label}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <ClipboardCheck className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Cochez vos assurances et lancez l'analyse</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Info */}
      <Card>
        <CardContent className="p-6">
          <h3 className="font-semibold font-['Manrope'] mb-4">Assurances obligatoires vs recommandées</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-red-600 mb-2">Obligatoires (selon activité)</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>• RC Pro : obligatoire pour les professions réglementées</li>
                <li>• Garantie décennale : obligatoire pour le BTP</li>
                <li>• Assurance auto pro : si véhicule professionnel</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-amber-600 mb-2">Fortement recommandées</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>• Mutuelle santé : pour compléter vos remboursements</li>
                <li>• Prévoyance : protège vos revenus en cas d'arrêt</li>
                <li>• Multirisque : si vous avez du matériel professionnel</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AssurancePage;
